// ─── Il listino, e la funzione che SCEGLIE IL PREZZO ─────────────────────────
//
// 🚨⭐⭐ IL PIANO LA CHIAMA «la regola più facile da sbagliare e la più difficile
// da accorgersene» (§ 06, Fase 3), e ha ragione due volte: un prezzo sbagliato
// non dà errore — esce dalla cassa come un numero qualunque e lo scopre chi
// quadra la serata, se lo scopre.
//
// 🔒 Modulo PURO: non conosce il database, non fa chiamate. Chi legge le righe
// è un'altra cosa da chi decide quale vince, e la seconda si prova da sola.
//
// 📏 IL LISTINO VERO DEL CIRCOLO, misurato su `cudi` il 15/09/2026: 39 fasce,
// tutte da 90 minuti, tutte sulla griglia base, prezzo **a giocatore** —
// 10,00 € di giorno e nei fine settimana, 12,00 € la sera, 13,00 € il picco
// (lun-ven 19:30-21:00). Quei numeri non sono inventati: nascono dai 3.336
// pagamenti veri arrivati da Matchpoint e sono stati confermati dal committente
// l'08/09/2026.
import { z } from 'zod';
import { dataSchema, giornoDellaData, giornoSchema, oraSchema } from './circolo.js';
/*
 * ⚠️ I due aiuti di data si IMPORTANO da `calendario`, non si riscrivono qui.
 * Una seconda aritmetica dei giorni diverge nel caso preciso per cui esiste —
 * il cambio dell'ora — e a divergere sarebbe la settimana su cui si leggono i
 * prezzi. (Nessun giro: `calendario` non conosce il listino.)
 */
import { indiceDelGiorno, piuGiorni } from './calendario.js';
import { ripartisciCents } from './money.js';
export const TIPI_PRENOTAZIONE = ['partita', 'lezione', 'torneo', 'corso'];
export const tipoPrenotazioneSchema = z.enum(TIPI_PRENOTAZIONE);
export const rigaListinoSchema = z.object({
    id: z.uuid(),
    bookingType: tipoPrenotazioneSchema,
    /** Nullo = ogni campo. */
    courtId: z.uuid().nullable(),
    /** Nullo = ogni giorno della settimana. */
    weekday: giornoSchema.nullable(),
    /** O tutte e due o nessuna: mezza fascia è una riga che nessuno sa applicare. */
    fromTime: oraSchema.nullable(),
    toTime: oraSchema.nullable(),
    durationMinutes: z.number().int(),
    /** 💶 In centesimi interi. Del campo, oppure a testa se `perPlayer`. */
    priceCents: z.number().int(),
    perPlayer: z.boolean(),
    validFrom: dataSchema.nullable(),
    validTo: dataSchema.nullable(),
});
export const creaRigaListinoSchema = z
    .object({
    bookingType: tipoPrenotazioneSchema,
    courtId: z.uuid().nullable().default(null),
    weekday: giornoSchema.nullable().default(null),
    fromTime: oraSchema.nullable().default(null),
    toTime: oraSchema.nullable().default(null),
    durationMinutes: z.number().int().min(5).max(600),
    priceCents: z.number().int().min(0),
    perPlayer: z.boolean().default(false),
    validFrom: dataSchema.nullable().default(null),
    validTo: dataSchema.nullable().default(null),
})
    .refine((v) => (v.fromTime === null) === (v.toTime === null), {
    message: 'La fascia oraria si mette intera, o non si mette',
    path: ['toTime'],
})
    .refine((v) => v.fromTime === null || v.toTime === null || v.toTime > v.fromTime, {
    message: 'La fascia oraria finisce prima di cominciare',
    path: ['toTime'],
})
    .refine((v) => v.validTo === null || v.validFrom === null || v.validTo >= v.validFrom, {
    message: 'Il periodo di validità finisce prima di cominciare',
    path: ['validTo'],
});
/**
 * Cambiare una riga già scritta — ⭐ **tutta**, non solo il prezzo.
 *
 * 🗣️ Nasce da una sua richiesta del 17/09/2026: *«soprattutto poter modificare
 * un listino»*. Qui c'erano sei campi e mancavano i quattro che DEFINISCONO la
 * riga — tipo, campo, giorno e durata — quindi spostare un prezzo dal lunedì al
 * martedì voleva dire cancellarlo e riscriverlo.
 *
 * ⛔ E cancellare-e-rifare non è lo stesso gesto in due tempi: nel mezzo quelle
 * ore restano **senza prezzo**, e una prenotazione che nasce lì dentro non dà
 * nessun errore — esce dalla cassa come un numero che non c'è. È la stessa
 * ragione per cui le Fasce hanno un pannello «Modifica», moltiplicata per il
 * fatto che qui il buco non si vede.
 *
 * ⚠️ Ciò che NON arriva non si tocca (`'campo' in m` nel deposito): un `null`
 * esplicito vuol dire «togli il criterio», assente vuol dire «lascia stare», e
 * le due cose non si possono confondere o un salvataggio parziale allargherebbe
 * la riga a tutti i casi.
 */
export const modificaRigaListinoSchema = z
    .object({
    bookingType: tipoPrenotazioneSchema.optional(),
    courtId: z.uuid().nullable().optional(),
    weekday: giornoSchema.nullable().optional(),
    durationMinutes: z.number().int().min(5).max(600).optional(),
    priceCents: z.number().int().min(0).optional(),
    perPlayer: z.boolean().optional(),
    fromTime: oraSchema.nullable().optional(),
    toTime: oraSchema.nullable().optional(),
    validFrom: dataSchema.nullable().optional(),
    validTo: dataSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' })
    /*
     * ⚠️ La fascia si cambia INTERA: o arrivano tutt'e due le ore, o nessuna.
     * Mezza fascia è una riga che nessuna funzione di scelta sa applicare — il
     * database la rifiuta lo stesso, ma qui il rifiuto ha le parole giuste.
     */
    .refine((v) => (v.fromTime === undefined) === (v.toTime === undefined), {
    message: 'La fascia oraria si cambia intera, o non si cambia',
    path: ['toTime'],
})
    .refine((v) => v.fromTime === undefined || v.toTime === undefined || (v.fromTime === null) === (v.toTime === null), { message: 'La fascia oraria si mette intera, o non si mette', path: ['toTime'] })
    .refine((v) => v.fromTime === undefined ||
    v.toTime === undefined ||
    v.fromTime === null ||
    v.toTime === null ||
    v.toTime > v.fromTime, { message: 'La fascia oraria finisce prima di cominciare', path: ['toTime'] })
    /* Le due date del periodo sono indipendenti: spostare solo la fine è lecito. */
    .refine((v) => v.validFrom === undefined ||
    v.validTo === undefined ||
    v.validFrom === null ||
    v.validTo === null ||
    v.validTo >= v.validFrom, { message: 'Il periodo di validità finisce prima di cominciare', path: ['validTo'] });
// ── La domanda e l'esito ────────────────────────────────────────────────────
/**
 * 🚨 I NUMERI ARRIVANO COME STRINGHE, e per questo si convertono (`coerce`).
 *
 * 📏 Trovato chiamando la rotta come la chiama il riquadro — cioè con una
 * querystring — e non rileggendo il codice: `durationMinutes=90` in un indirizzo
 * è il **testo** `"90"`, e uno schema che pretende un numero lo rifiuta. ⇒ La
 * prova del prezzo non avrebbe funzionato da nessun browser, e la funzione
 * sotto era giusta: a non funzionare era la porta.
 * 📌 *Uno schema si prova con la forma in cui i dati arrivano davvero, non con
 *    quella in cui li scriveremmo noi.*
 */
export const domandaPrezzoSchema = z.object({
    bookingType: tipoPrenotazioneSchema,
    courtId: z.uuid().nullable().default(null),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.coerce
        .number('Serve una durata in minuti')
        .int('La durata è in minuti interi')
        .min(5, 'Una durata sotto i 5 minuti non è una partita')
        .max(600),
    /** Quanti in campo: serve solo a dire le quote, non a scegliere la riga. */
    players: z.coerce
        .number('Serve il numero di giocatori')
        .int('I giocatori si contano interi')
        .min(1, 'In campo c’è almeno una persona')
        .max(20)
        .default(4),
});
export const esitoPrezzoSchema = z.object({
    riga: rigaListinoSchema.nullable(),
    /** Quanto costa il CAMPO, in centesimi. `null` = non lo sappiamo. */
    prezzoCampoCents: z.number().int().nullable(),
    /** Le quote a testa: la somma fa sempre il totale, senza centesimi persi. */
    quoteGiocatoriCents: z.array(z.number().int()),
    /** Tutte le righe che si applicavano, in ordine: la prima è quella che vince. */
    applicabili: z.array(rigaListinoSchema),
    /**
     * ⚠️ Più righe si contendono il primo posto e la regola NON le sa ordinare.
     * Una risposta viene data lo stesso (serve un prezzo), ma va detto.
     */
    ambigua: z.boolean(),
    concorrenti: z.array(rigaListinoSchema),
    /** La frase da mettere sotto il numero, già in italiano. */
    perche: z.string(),
});
// ── La regola ───────────────────────────────────────────────────────────────
/**
 * ⚖️ L'ora d'inizio deve stare DENTRO la fascia, estremo finale escluso.
 *
 * 📏 La riga viene dal gestionale di oggi, dove è costata la voce 188: cercare
 * la fascia che *comincia* a quell'ora lasciava senza prezzo 6 prenotazioni su
 * 32 — fra cui una del committente — mentre il prezzo esisteva. E `< fine` e
 * non `<= fine`: l'ora uguale alla fine appartiene alla fascia DOPO.
 */
export function oraDentroLaFascia(inizio, fine, ora) {
    if (inizio === null || fine === null)
        return true; // nessuna fascia = tutto il giorno
    return ora >= inizio && ora < fine;
}
/** Una riga si applica al caso? Nessuna graduatoria qui: solo sì o no. */
export function siApplica(r, d, giorno) {
    if (r.bookingType !== d.bookingType)
        return false;
    if (r.durationMinutes !== d.durationMinutes)
        return false;
    if (r.courtId !== null && r.courtId !== d.courtId)
        return false;
    if (r.weekday !== null && r.weekday !== giorno)
        return false;
    if (!oraDentroLaFascia(r.fromTime, r.toTime, d.time))
        return false;
    if (r.validFrom !== null && d.date < r.validFrom)
        return false;
    if (r.validTo !== null && d.date > r.validTo)
        return false;
    return true;
}
/**
 * I tre assi della specificità, nell'ordine del § 01.4.
 *
 * ⚠️ La VALIDITÀ non è qui dentro, ed è una decisione: vedi `strato()`.
 */
export function specificita(r) {
    return [r.courtId !== null ? 1 : 0, r.fromTime !== null ? 1 : 0, r.weekday !== null ? 1 : 0];
}
/**
 * ⭐⭐ IL PERIODO NON È UN ASSE: È UNO STRATO, e sta SOPRA la specificità.
 *
 * 🗣️ Il committente l'ha chiesto per esteso l'08/09/2026 — «fare dei prezzi
 * per l'estate e dei prezzi per l'inverno» — e il gestionale di oggi gliel'ha
 * dato con una frase che vale come specifica: *«Estate e Inverno non sono due
 * listini sopra la stessa griglia: sono due griglie complete»*.
 * ⇒ Un periodo **sostituisce** ciò che c'era, non ci compete contro. Fra le
 *   righe che si applicano vince lo strato con il periodo più stretto, e
 *   dentro quello strato — e solo lì — si guarda chi è più specifica.
 *
 * ⛔ Trattarlo come un quarto asse sembra più semplice ed è sbagliato: una riga
 * «estate, tutti i giorni» e una «di sempre, lunedì sera» non si dominerebbero
 * a vicenda, e il listino stagionale risulterebbe **ambiguo ogni lunedì sera**
 * — cioè il meccanismo direbbe «non lo so» proprio quando funziona.
 *
 * Il numero è l'ampiezza in giorni: più stretto = più forte. `Infinity` è la
 * griglia senza date, quella che vale quando nessun periodo copre il giorno.
 */
export function strato(r) {
    return ampiezzaValidita(r);
}
/**
 * `a` è più specifica di `b` su OGNI asse, e strettamente su almeno uno?
 *
 * 🚨⭐⭐ QUI STA LA PARTE CHE NON SI VEDE, ed è il motivo per cui non si usa un
 * punteggio. Con un punteggio, «Campo 4, sempre» e «tutti i campi, ma solo il
 * venerdì sera» si sommano a due numeri, uno dei due vince, e nessuno sa che
 * c'era una gara: la regola del piano mette a confronto i due stati dello
 * STESSO asse («campo indicato > campo nullo»), non due assi diversi.
 * ⇒ Quando nessuna delle due domina l'altra, la risposta onesta non è «vince
 *   questa»: è **«il listino non lo dice»**, e chi lo ha scritto deve saperlo.
 *
 * ⚖️ Si confrontano righe dello STESSO strato: fra strati diversi ha già deciso
 * il periodo, e mescolarli riporterebbe il difetto che `strato()` toglie.
 */
function domina(a, b) {
    const x = specificita(a);
    const y = specificita(b);
    let almenoUna = false;
    for (let i = 0; i < x.length; i += 1) {
        if (x[i] < y[i])
            return false;
        if (x[i] > y[i])
            almenoUna = true;
    }
    return almenoUna;
}
/** Quanti giorni copre la validità: `Infinity` se non ha estremi. */
function ampiezzaValidita(r) {
    if (r.validFrom === null || r.validTo === null)
        return Number.POSITIVE_INFINITY;
    return (Date.parse(`${r.validTo}T00:00:00Z`) - Date.parse(`${r.validFrom}T00:00:00Z`)) / 86_400_000;
}
/** Quanti minuti copre la fascia oraria: `Infinity` se non c'è. */
function ampiezzaFascia(r) {
    if (r.fromTime === null || r.toTime === null)
        return Number.POSITIVE_INFINITY;
    return minuti(r.toTime) - minuti(r.fromTime);
}
function minuti(ora) {
    const [h, m] = ora.split(':');
    return Number(h) * 60 + Number(m);
}
/**
 * L'ordine in cui si mostrano le righe applicabili: la prima è quella che
 * vince. Fra pari grado si sceglie la più stretta, e in ultimo l'`id` — che è
 * **arbitrario**, e sta qui solo perché una risposta che cambia da sola sarebbe
 * peggio di una risposta discutibile. Quando si arriva a usarlo, `ambigua` è
 * già vero.
 */
function inOrdine(a, b) {
    const sa = strato(a);
    const sb = strato(b);
    if (sa !== sb)
        return sa - sb;
    const x = specificita(a);
    const y = specificita(b);
    for (let i = 0; i < x.length; i += 1) {
        if (x[i] !== y[i])
            return y[i] - x[i];
    }
    const fa = ampiezzaFascia(a);
    const fb = ampiezzaFascia(b);
    if (fa !== fb)
        return fa - fb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/**
 * Il prezzo di un caso, fra tutte le righe del listino.
 *
 * ⛔ `null` vuol dire **«non lo sappiamo»**, e non è zero. Zero vorrebbe dire
 * «gratis», e una partita segnata gratis non la rivede più nessuno: è la stessa
 * distinzione che il gestionale di oggi ha imparato con la voce 180.
 */
export function scegliPrezzo(righe, domanda) {
    const giorno = giornoDellaData(domanda.date);
    if (giorno === null) {
        return vuoto('Quella data non è una data.');
    }
    const applicabili = righe.filter((r) => siApplica(r, domanda, giorno)).sort(inOrdine);
    if (applicabili.length === 0) {
        return vuoto(`Nessuna riga del listino copre ${domanda.bookingType} di ${domanda.durationMinutes} minuti ` +
            `alle ${domanda.time} di quel giorno.`);
    }
    // ① lo strato: il periodo più stretto fra quelli che coprono il giorno.
    const piuStretto = Math.min(...applicabili.map(strato));
    const inGara = applicabili.filter((r) => strato(r) === piuStretto);
    // ② dentro lo strato, la frontiera: le righe che nessun'altra domina. Se ne
    //    resta una sola la regola ha deciso; se ne restano due NON decide, e si dice.
    const frontiera = inGara.filter((a) => !inGara.some((b) => b !== a && domina(b, a)));
    const vincitrice = applicabili[0];
    const ambigua = frontiera.length > 1;
    const campo = vincitrice.perPlayer ? vincitrice.priceCents * domanda.players : vincitrice.priceCents;
    return {
        riga: vincitrice,
        prezzoCampoCents: campo,
        quoteGiocatoriCents: vincitrice.perPlayer
            ? Array.from({ length: domanda.players }, () => vincitrice.priceCents)
            : ripartisciCents(campo, domanda.players),
        applicabili,
        ambigua,
        concorrenti: ambigua ? frontiera : [],
        perche: ambigua
            ? `${frontiera.length} righe si contendono il primo posto e il listino non dice quale vince: ` +
                `ne è stata scelta una per non restare senza prezzo, ma vanno separate.`
            : spiega(vincitrice, applicabili.length),
    };
}
function vuoto(perche) {
    return {
        riga: null,
        prezzoCampoCents: null,
        quoteGiocatoriCents: [],
        applicabili: [],
        ambigua: false,
        concorrenti: [],
        perche,
    };
}
function spiega(r, quante) {
    const pezzi = [];
    if (r.validFrom !== null || r.validTo !== null)
        pezzi.push('vale in un periodo dichiarato, che copre la griglia di sempre');
    if (r.courtId !== null)
        pezzi.push('è di quel campo');
    if (r.fromTime !== null)
        pezzi.push(`copre la fascia ${r.fromTime}-${r.toTime}`);
    if (r.weekday !== null)
        pezzi.push('è di quel giorno della settimana');
    if (pezzi.length === 0)
        pezzi.push('è la riga generale, e non ce n’erano di più specifiche');
    const fra = quante > 1 ? `Fra ${quante} righe applicabili vince questa perché ` : 'Vince perché ';
    return `${fra}${pezzi.join(', ')}.`;
}
/**
 * Le coppie di righe che si applicano allo stesso caso e nessuna delle due
 * domina l'altra: sono i punti dove il listino dà due risposte.
 *
 * ⚖️ Non è la stessa cosa di «due righe che si sovrappongono»: che una riga
 * generale e una specifica si sovrappongano è NORMALE — è il meccanismo. Il
 * guaio è la sovrapposizione fra PARI GRADO, ed è quella che si vede solo
 * mettendole in fila una accanto all'altra.
 */
export function trovaConflitti(righe) {
    const out = [];
    for (let i = 0; i < righe.length; i += 1) {
        for (let j = i + 1; j < righe.length; j += 1) {
            const a = righe[i];
            const b = righe[j];
            if (!siSovrappongono(a, b))
                continue;
            // Strati diversi: ha già deciso il periodo, non c'è niente da separare.
            if (strato(a) !== strato(b))
                continue;
            if (domina(a, b) || domina(b, a))
                continue;
            out.push({
                a,
                b,
                perche: specificita(a).join() === specificita(b).join()
                    ? 'Stessa specificità e stesso perimetro: quale vince dipende da come sono ordinate.'
                    : 'Una è più specifica su un aspetto, l’altra su un altro: la regola non le sa confrontare.',
            });
        }
    }
    return out;
}
function siSovrappongono(a, b) {
    if (a.bookingType !== b.bookingType)
        return false;
    if (a.durationMinutes !== b.durationMinutes)
        return false;
    if (a.courtId !== null && b.courtId !== null && a.courtId !== b.courtId)
        return false;
    if (a.weekday !== null && b.weekday !== null && a.weekday !== b.weekday)
        return false;
    if (!fasceSiToccano(a, b))
        return false;
    return periodiSiToccano(a, b);
}
function fasceSiToccano(a, b) {
    if (a.fromTime === null || b.fromTime === null)
        return true;
    return a.fromTime < b.toTime && b.fromTime < a.toTime;
}
function periodiSiToccano(a, b) {
    const da = (r) => r.validFrom ?? '0000-01-01';
    const al = (r) => r.validTo ?? '9999-12-31';
    // Estremi COMPRESI: un periodo che finisce il 31/08 e uno che comincia il
    // 31/08 si toccano davvero, e quel giorno ha due prezzi.
    return da(a) <= al(b) && da(b) <= al(a);
}
/**
 * L'elenco del listino porta con sé i suoi conflitti.
 *
 * ⚖️ Non è una rotta a parte, ed è il punto: un difetto che bisogna andare a
 * cercare è un difetto che nessuno cerca. Chi apre il listino lo vede.
 */
export const conflittoSchema = z.object({
    a: rigaListinoSchema,
    b: rigaListinoSchema,
    perche: z.string(),
});
export const elencoListinoSchema = z.object({
    items: z.array(rigaListinoSchema),
    conflitti: z.array(conflittoSchema),
});
// ── ⭐⭐ LA SETTIMANA DEI PREZZI ───────────────────────────────────────────────
//
// 🗣️ Nasce dalla sua richiesta del 17/09/2026 — *«semplificarmi la creazione dei
// listini… come abbiamo fatto fasce»*. Nelle Fasce la metà che semplifica non è
// il modulo: è la **striscia**, cioè il disegno che fa vedere i buchi. Una
// tabella di righe non dice mai se un'ora è scoperta; una griglia sì.
//
// 🚨⭐⭐ E QUI IL BUCO È PEGGIO CHE NELLE FASCE, che è la ragione per cui questa
// griglia conta più di quella. Un'ora senza fascia si vede subito — il
// calendario la disegna in grigio. Un'ora senza PREZZO non si vede affatto: la
// prenotazione nasce lo stesso, senza importo e senza errore, e lo scopre chi
// quadra la cassa a fine serata, se lo scopre.
//
// ⚖️ E si calcola con `scegliPrezzo`, la stessa funzione che mette l'importo
// addosso a una prenotazione — mai con una seconda regola «per disegnare». Una
// mappa che non usa la strada vera non è un'anteprima: è una seconda strada, e
// diverge nel caso preciso per cui la si guardava.
/**
 * Ogni mezz'ora, e non ogni ora.
 *
 * ⚠️ Non è una scelta di densità: il listino vero del circolo ha una fascia che
 * comincia alle **19:30** (il picco 19:30–21:00, misurato l'08/09/2026). Una
 * griglia oraria mostrerebbe le 19:00 e le 20:00 e salterebbe il confine —
 * cioè disegnerebbe una banda di prezzo che comincia un'ora prima di dove
 * comincia davvero. Ed è anche il passo con cui il circolo fa cominciare le
 * partite (le fasce prenotabili, `slot_minutes`).
 */
export const PASSO_SETTIMANA_PREZZI = 30;
export const cellaPrezzoSchema = z.object({
    time: oraSchema,
    /**
     * 💶 Il numero da FAR VEDERE: a testa se la riga è a testa, del campo se no.
     * ⚖️ È il numero che il circolo riconosce — il suo listino è a giocatore, e
     * una griglia piena di 40 e 52 invece di 10 e 13 andrebbe riletta ogni volta.
     * Il totale sta accanto (`courtCents`), non al posto suo.
     */
    priceCents: z.number().int().nullable(),
    courtCents: z.number().int().nullable(),
    perPlayer: z.boolean(),
    /** Quale riga ha vinto: serve a dire «questa mezz'ora la decide quella riga». */
    rowId: z.uuid().nullable(),
    ambigua: z.boolean(),
});
export const giornoPrezziSchema = z.object({
    date: dataSchema,
    weekday: giornoSchema,
    celle: z.array(cellaPrezzoSchema),
});
export const settimanaPrezziSchema = z.object({
    /** Il lunedì della settimana guardata. */
    dal: dataSchema,
    /** Le ore, dall'apertura alla chiusura: sono le righe della griglia. */
    ore: z.array(oraSchema),
    giorni: z.array(giornoPrezziSchema),
    /** Quante mezz'ore non hanno un prezzo. Zero è la risposta che si spera. */
    scoperte: z.number().int(),
    ambigue: z.number().int(),
});
export const domandaSettimanaSchema = z.object({
    /** Un giorno QUALUNQUE della settimana voluta: il lunedì lo trova da sé. */
    date: dataSchema,
    bookingType: tipoPrenotazioneSchema.default('partita'),
    courtId: z.uuid().nullable().default(null),
    durationMinutes: z.coerce.number().int().min(5).max(600).default(90),
    players: z.coerce.number().int().min(1).max(20).default(4),
});
/**
 * La settimana tipo, prezzo per prezzo.
 *
 * ⭐⭐ **VUOLE UNA DATA VERA, e non è un dettaglio tecnico.** Le fasce
 * prenotabili disegnano «la settimana tipo» e per questo la loro striscia non
 * sa applicare i periodi di validità (limite dichiarato, lì). Qui i periodi
 * sono lo STRATO che governa tutto il resto — estate e inverno sono due griglie
 * complete — quindi una griglia senza data non potrebbe rispondere affatto.
 * ⇒ Si guarda **una settimana precisa**, e cambiando settimana si vede il
 *   listino stagionale cambiare sotto gli occhi: è la sola prova che i periodi
 *   fanno quello che dicono.
 */
export function settimanaDeiPrezzi(righe, d, apre, chiude) {
    const dal = piuGiorni(d.date, -indiceDelGiorno(d.date));
    const da = minuti(apre);
    const a = minuti(chiude);
    const ore = [];
    for (let t = da; t < a; t += PASSO_SETTIMANA_PREZZI)
        ore.push(testoOra(t));
    let scoperte = 0;
    let ambigue = 0;
    const giorni = Array.from({ length: 7 }, (_, i) => {
        const date = piuGiorni(dal, i);
        const celle = ore.map((time) => {
            const e = scegliPrezzo(righe, {
                bookingType: d.bookingType,
                courtId: d.courtId,
                date,
                time,
                durationMinutes: d.durationMinutes,
                players: d.players,
            });
            if (e.prezzoCampoCents === null)
                scoperte += 1;
            if (e.ambigua)
                ambigue += 1;
            return {
                time,
                priceCents: e.riga === null ? null : e.riga.perPlayer ? e.riga.priceCents : e.prezzoCampoCents,
                courtCents: e.prezzoCampoCents,
                perPlayer: e.riga?.perPlayer ?? false,
                rowId: e.riga?.id ?? null,
                ambigua: e.ambigua,
            };
        });
        return { date, weekday: giornoDellaData(date) ?? 0, celle };
    });
    return { dal, ore, giorni, scoperte, ambigue };
}
function testoOra(t) {
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
//# sourceMappingURL=listino.js.map